import { IAffiliateDetails } from '../../../common/contexts/ApplicationContext';
import { ISale, ISaleType } from '../../../common/types/apiSchema';
import { IPriceValueProps } from '../../PriceValue';
import { SaleCardVariant } from '../SaleCard.interfaces';

export interface IDiscountBadgeConfigurationOutput {
  discountTooltip: string;
  discountValue: string;
  showRegularBadge: boolean;
  showUrgencyBadge: boolean;
  showTooltip: boolean;
}

export interface IPriceValueConfigurationOutput {
  showPriceValue: boolean;
  priceValueProperties: IPriceValueProps;
}

type TShowPrices = (userId: string, isOpenSite?: boolean) => boolean;

type TGetDiscountBadges = (
  sale: ISale,
  variant: SaleCardVariant,
  showTooltip?: boolean
) => IDiscountBadgeConfigurationOutput;

export const getDiscountBadge: TGetDiscountBadges = (sale, variant, showTooltip = true) => {
  const minDiscountForUrgency: number = 45;

  const isNormalVariant: boolean = variant === SaleCardVariant.normal;

  let discountBadgeConfiguration: IDiscountBadgeConfigurationOutput = {
    discountTooltip: '',
    discountValue: null,
    showRegularBadge: false,
    showTooltip: false,
    showUrgencyBadge: false,
  };

  if (sale.prices) {
    /* AB test DEV-34786-booking-fees-phase-2 (START)*/
    let discount: number = sale.prices.discount;
    if (priceModificationExperimentEnabled(sale)) {
      discount = priceModificationExperimentDiscount(discount);
    }
    /* AB test DEV-34786-booking-fees-phase-2 (END)*/
    discountBadgeConfiguration = {
      discountTooltip: sale.prices.discountTooltip,
      discountValue: discount && discount.toString(),
      showRegularBadge: discount > 0 && sale.prices.pricingRules.showDiscount,
      showTooltip: sale.prices.discountTooltip && showTooltip ? true : false,
      showUrgencyBadge:
        isNormalVariant &&
        sale.prices.pricingRules.showDiscount &&
        sale.prices.discount >= minDiscountForUrgency,
    };
  }

  return discountBadgeConfiguration;
};

export const getPriceValue: (
  sale: ISale,
  userId: string,
  affiliateDetails: IAffiliateDetails
) => IPriceValueConfigurationOutput = (sale, userId, affiliateDetails) => {
  const showPriceValue: boolean =
    sale.prices &&
    sale.prices.pricingRules.showPrices &&
    userLoggedInOrOpenSite(userId, affiliateDetails.isOpenSite);

  let priceValue: IPriceValueConfigurationOutput = {
    priceValueProperties: {
      hideRackRate: false,
      leadRate: null,
      rackRate: null,
      showTooltip: false,
      translationData: null,
    },
    showPriceValue,
  };

  if (showPriceValue) {
    let displayedPrice: string = sale.prices.leadRate.forDisplay;
    /* AB test DEV-34786-booking-fees-phase-2 (START)*/

    if (priceModificationExperimentEnabled(sale)) {
      displayedPrice = priceModificationExperimentPrice(displayedPrice);
    } else if (priceModificationRoundingExperimentEnabled(sale)) {
      displayedPrice = priceModificationRoundingExperimentPrice(displayedPrice);
    }

    /* AB test DEV-34786-booking-fees-phase-2 (END)*/

    priceValue = {
      priceValueProperties: {
        hideRackRate: !sale.prices.pricingRules.showRackRate,
        leadRate: displayedPrice,
        rackRate: sale.prices.rackRate.forDisplay,
        showTooltip: sale.prices.leadRateTooltip ? true : false,
        translationData: {
          leadRateLabel: sale.prices.leadRateLabel,
          leadRateTooltip: sale.prices.leadRateTooltip,
          leadRateUnitLabel: sale.prices.leadRateUnitLabel,
        },
      },
      showPriceValue,
    };
  }

  return priceValue;
};

/* AB test DEV-34786-booking-fees-phase-2 helper methods (Start)*/
const priceModificationExperimentEnabled: (sale?: ISale) => boolean = sale => {
  if (sale && sale.type === ISaleType.wrd) {
    return false;
  }
  if (window.location.href.indexOf('gce_pbf=1') === -1) {
    return false;
  }
  const displayedPrice: string = sale.prices.leadRate.forDisplay;
  const currentAmount: number = parseFloat(displayedPrice.replace(/[^0-9.]/g, ''));
  const testNines: (str: string) => boolean = str => /9{1}$/.test(str);

  return testNines(currentAmount.toString()) === false;
};

const priceModificationExperimentPrice: (displayedPrice: string) => string = displayedPrice => {
  const percentModifier: number = 0.018;
  const currentAmount: number = parseFloat(displayedPrice.replace(/[^0-9.]/g, ''));

  if (currentAmount) {
    let modifiedAmount: string = `${Math.ceil(currentAmount * (1.0 + percentModifier))}`;
    const THOUSAND_SEPARATOR_POSITION: number = 3;
    // add thousand separator
    if (modifiedAmount.length > THOUSAND_SEPARATOR_POSITION) {
      const splitPosition: number = modifiedAmount.length - THOUSAND_SEPARATOR_POSITION;
      modifiedAmount = `${modifiedAmount.substr(0, splitPosition)},${modifiedAmount.substr(
        splitPosition
      )}`;
    }

    return displayedPrice.replace(/[\d,\.]+/, modifiedAmount);
  }

  return displayedPrice;
};

const priceModificationExperimentDiscount: (discount: number) => number = discount => {
  // tslint:disable-next-line: no-magic-numbers
  if (discount && discount > 2) {
    // tslint:disable-next-line: no-magic-numbers
    return discount < 45 ? discount - 2 : discount - 1;
  }

  return discount;
};
/* AB test DEV-34786-booking-fees-phase-2 (END)*/
/* AB test DEV-49104-booking-fees-rounding (START)*/
const priceModificationRoundingExperimentEnabled: (sale?: ISale) => boolean = sale => {
  if (sale && (sale.type === ISaleType.wrd || sale.id[0] !== 'A')) {
    return false;
  }
  if (window.location.href.indexOf('gce_rbf=1') === -1) {
    return false;
  }

  return true;
};

const priceModificationRoundingExperimentPrice: (
  displayedPrice: string
) => string = displayedPrice => {
  const modifiedAmountNumber: number = computePriceModification(displayedPrice);
  if (modifiedAmountNumber) {
    let modifiedAmount: string = `${modifiedAmountNumber}`;
    const THOUSAND_SEPARATOR_POSITION: number = 3;
    // add thousand separator
    if (modifiedAmount.length > THOUSAND_SEPARATOR_POSITION) {
      const splitPosition: number = modifiedAmount.length - THOUSAND_SEPARATOR_POSITION;
      const thousandsCharacter: string = displayedPrice.includes(',') ? ',' : '.';

      const pricePrefix: string = modifiedAmount.substr(0, splitPosition);
      const priceSuffix: string = modifiedAmount.substr(splitPosition);
      modifiedAmount = `${pricePrefix}${thousandsCharacter}${priceSuffix}`;
    }

    return displayedPrice.replace(/[\d,\.]+/, modifiedAmount);
  }

  return displayedPrice;
};

const readValueFromDisplayPrice: (displayPrice: string) => number = displayPrice => {
  return parseFloat(displayPrice.replace(/[^0-9]/g, ''));
};

const computePriceModification: (displayedPrice: string) => number = displayedPrice => {
  let modifiedAmountNumber: number;
  const currentAmount: number = readValueFromDisplayPrice(displayedPrice);
  const decimalBase: number = 10;
  const endingFive: number = 5;
  const endingNine: number = 9;
  const offsetToNextNumberEndingInFive: number = 6;

  if (currentAmount) {
    const digitsWithoutLast: number = Math.trunc(currentAmount / decimalBase);
    const lastDigit: number = currentAmount % decimalBase;
    // tslint:disable-next-line: no-magic-numbers
    if (lastDigit >= 0 && lastDigit <= 4) {
      modifiedAmountNumber = digitsWithoutLast * decimalBase + endingFive;
      // tslint:disable-next-line: no-magic-numbers
    } else if (lastDigit >= 5 && lastDigit <= 8) {
      modifiedAmountNumber = digitsWithoutLast * decimalBase + endingNine;
    } else {
      // else it's 9
      modifiedAmountNumber = currentAmount + offsetToNextNumberEndingInFive;
    }
  }

  return modifiedAmountNumber;
};

/* AB test DEV-49104-booking-fees-rounding (END)*/
export const userLoggedInOrOpenSite: TShowPrices = (userId, isOpenSite = false) => {
  return (userId !== undefined && userId !== null && userId !== '') || isOpenSite;
};
